import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import toast from "react-hot-toast";
import * as FiIcons from "react-icons/fi";
const {
  FiUser,
  FiMail,
  FiLock,
  FiShield,
  FiArrowRight,
  FiCheckCircle,
  FiInfo,
} = FiIcons;
import SafeIcon from "../common/SafeIcon";
import { FEATURE_FLAGS } from "../lib/featureFlags";

export default function SignUp() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    role: "patient",
  });
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please agree to the data protection agreement.");
      return;
    }
    setLoading(true);
    const result = await signUp(formData);
    if (result.success) {
      toast.success("User account created. Please verify your email.");
      navigate("/signin");
    } else {
      toast.error(result.error || "Registration Failed");
    }
    setLoading(false);
  };

  const handleRoleSelect = (roleId) => {
    setFormData({ ...formData, role: roleId });
  };

  // --- V1 FALLBACK UI ---
  if (!FEATURE_FLAGS.FEATURE_AUTH_UI_V2) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
          <h1 className="text-2xl font-bold text-white mb-6 text-center">
            Create Identity Profile (V1)
          </h1>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="First Name"
              value={formData.first_name}
              onChange={(e) =>
                setFormData({ ...formData, first_name: e.target.value })
              }
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white"
              required
            />
            <input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white"
              required
            />
            <button
              type="submit"
              className="w-full bg-primary-600 py-3 rounded-xl text-white font-bold"
            >
              Register
            </button>
          </form>
          <p className="mt-4 text-center text-slate-400 text-sm">
            Already have an account?{" "}
            <Link to="/signin" className="text-primary-400">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // --- V2 MODERN UI ---
  return (
    <div className="min-h-screen bg-[#020617] flex overflow-hidden relative">
      <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-500/10 blur-[120px] rounded-full" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full" />

      {/* Main Content Container */}
      <div className="w-full flex items-center justify-center p-6 md:p-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-[700px] w-full grid grid-cols-1 lg:grid-cols-12 gap-12 bg-slate-900/20 border border-slate-800/50 backdrop-blur-2xl rounded-[3rem] p-8 md:p-12 shadow-2xl"
        >
          {/* Form Side */}
          <div className="lg:col-span-12">
            <div className="mb-10 flex flex-col items-center">
              <Link to="/" className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiShield} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-black tracking-tighter text-white">
                  iVivaCore
                </span>
              </Link>
              <h1 className="text-3xl font-black text-white text-center">
                Institutional Enrollment
              </h1>
              <p className="text-slate-400 text-sm font-medium mt-2">
                Initialize your biological monitoring profile.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Role Selection */}
              <div className="space-y-3">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                  Access Tier
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    {
                      id: "patient",
                      label: "Patient",
                      icon: FiUser,
                      desc: "Personal monitoring",
                    },
                    {
                      id: "clinician",
                      label: "Clinician",
                      icon: FiShield,
                      desc: "Medical oversight",
                    },
                  ].map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => handleRoleSelect(role.id)}
                      className={`p-4 rounded-2xl border text-left transition-all ${
                        formData.role === role.id
                          ? "bg-primary-500/10 border-primary-500 shadow-lg shadow-primary-500/10"
                          : "bg-slate-900/50 border-slate-800 hover:border-slate-700"
                      }`}
                    >
                      <SafeIcon
                        icon={role.icon}
                        className={`text-xl mb-3 ${formData.role === role.id ? "text-primary-500" : "text-slate-500"}`}
                      />
                      <p
                        className={`font-black text-xs uppercase tracking-widest ${formData.role === role.id ? "text-white" : "text-slate-400"}`}
                      >
                        {role.label}
                      </p>
                      <p className="text-[10px] text-slate-500 mt-1 font-medium">
                        {role.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Personal Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    required
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:border-primary-500/50 outline-none transition-all font-medium"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    required
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:border-primary-500/50 outline-none transition-all font-medium"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                  Institutional Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:border-primary-500/50 outline-none transition-all font-medium"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                  Secret Key
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                  placeholder="Min. 8 characters"
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:border-primary-500/50 outline-none transition-all font-medium font-mono"
                />
              </div>

              {/* Consent */}
              <div
                className="bg-slate-950/50 border border-slate-800 rounded-2xl p-5 flex gap-4 cursor-pointer hover:bg-slate-900/50 transition-colors"
                onClick={() => setAgreed(!agreed)}
              >
                <div
                  className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${agreed ? "bg-primary-500 border-primary-500" : "border-slate-700"}`}
                >
                  {agreed && (
                    <SafeIcon
                      icon={FiCheckCircle}
                      className="text-white text-sm"
                    />
                  )}
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 leading-relaxed font-medium">
                    I agree to the{" "}
                    <span className="text-primary-500 font-bold">
                      Data Processing Agreement
                    </span>{" "}
                    and acknowledge that my biological data will be monitored by
                    the iVivaCore system.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-600 hover:bg-primary-500 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-xl shadow-primary-900/20 active:scale-[0.98] disabled:opacity-50 group"
              >
                {loading ? (
                  "Initializing..."
                ) : (
                  <>
                    <SafeIcon
                      icon={FiArrowRight}
                      className="group-hover:translate-x-1 transition-transform"
                    />{" "}
                    Initialize Identity
                  </>
                )}
              </button>
            </form>

            <div className="mt-10 text-center">
              <p className="text-sm text-slate-400 font-medium">
                Already registered?{" "}
                <Link
                  to="/signin"
                  className="text-primary-500 font-black uppercase tracking-widest text-xs ml-2 hover:text-primary-400 transition-colors"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
